import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { ArrowUpCircle } from './icons';
import { t } from '../i18n';
import { colors, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { ouvrirLien } from '../utils/ouvrirLien';

interface UpdateBannerProps {
  storeUrl: string;
  onDismiss: () => void;
  testID?: string;
}

/**
 * Bandeau « une nouvelle version est disponible » — ignorable.
 *
 * FORME REPRISE DE `SubscriptionExpiryNotice`, l'encart de la maison : pastille d'icône carrée à
 * gauche, titre et texte à droite, mêmes espacements. Rien n'est dessiné pour ce composant seul —
 * l'écart avec le modèle tient à deux choses, et chacune se justifie.
 *
 * `shadow.floating` PLUTÔT QUE `shadow.card` : le bandeau ne repose pas dans le flux, il flotte
 * au-dessus d'un écran quelconque. C'est exactement ce que ce token désigne, et l'ombre plus
 * marquée est ce qui le détache d'un fond qu'on ne connaît pas à l'avance.
 *
 * FOND BLANC ET NON TEINTÉ : le modèle se pose sur le fond gris clair d'un écran, où sa teinte
 * douce ressort. Ici le bandeau peut passer au-dessus de n'importe quelle couleur — une carte
 * blanche bordée de `grey200` reste lisible partout, une teinte pâle non.
 *
 * LES ACTIONS SONT SUR LEUR PROPRE LIGNE, alignées à droite. Serrées contre le texte dans une
 * troisième colonne, elles écrasaient la phrase sur deux caractères de large sur iPhone SE.
 *
 * DEUX ZONES DE TAP DISTINCTES, et rien d'autre de cliquable : un bandeau entièrement pressable
 * ferait ouvrir le store à quelqu'un qui visait « Plus tard ».
 *
 * `Linking.openURL` ET NON LE NAVIGATEUR INTÉGRÉ — l'inverse du choix fait pour les articles du fil
 * News (`NewsScreen`), et délibérément. Un lien de store ouvert dans une vue web affiche une page
 * depuis laquelle on ne peut pas installer : il faut basculer vers l'application App Store ou Play
 * Store, ce que seul `openURL` fait.
 */
export function UpdateBanner({ storeUrl, onDismiss, testID = 'update-banner' }: UpdateBannerProps) {
  return (
    <View style={styles.banniere} testID={testID}>
      <View style={styles.entete}>
        <View style={styles.pastille}>
          <ArrowUpCircle size={20} color={colors.bluePrimary} strokeWidth={2} />
        </View>

        <View style={styles.texte}>
          <Text style={styles.titre}>{t('update.banner.title')}</Text>
          <Text style={styles.corps}>{t('update.banner.body')}</Text>
        </View>
      </View>

      <View style={styles.actions}>
        <Pressable
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel={t('update.banner.dismissA11y')}
          testID={`${testID}-dismiss`}
          style={styles.rejet}
        >
          <Text style={styles.rejetTexte}>{t('update.banner.dismiss')}</Text>
        </Pressable>

        <Pressable
          onPress={() => ouvrirLien(storeUrl)}
          accessibilityRole="button"
          testID={`${testID}-action`}
          style={styles.action}
        >
          <Text style={styles.actionTexte}>{t('update.banner.action')}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  banniere: {
    marginHorizontal: screenPadding,
    marginTop: spacing.sm,
    padding: spacing.lg,
    gap: spacing.md,
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.grey200,
    ...shadow.floating,
  },
  entete: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  // Pastille du modèle maison : carré arrondi, teinte douce de l'accent, icône vive au centre.
  pastille: {
    width: 40,
    height: 40,
    borderRadius: radius.md,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  texte: { flex: 1, gap: 2 },
  titre: {
    fontSize: typography.bodyMd.size,
    fontWeight: typography.bodyMd.weight,
    lineHeight: typography.bodyMd.lineHeight,
    color: colors.grey900,
  },
  corps: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    color: colors.grey500,
  },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: spacing.sm },
  // Même hauteur d'appui que l'action, pour que les deux libellés s'alignent sur leur ligne de base.
  rejet: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  rejetTexte: { fontSize: typography.small.size, fontWeight: '500', color: colors.grey500 },
  // Métriques de `Chip` : pilule, teinte pleine de l'accent primaire.
  action: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.bluePrimary,
    borderRadius: radius.pill,
  },
  actionTexte: { fontSize: typography.small.size, fontWeight: '600', color: colors.white },
});
